
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import './Profile.css';
import Button from '../components/Button';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import ConfirmationModal from '../components/ConfirmationModal';

const Profile = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        role: 'customer'
    });

    const [kycDocs, setKycDocs] = useState([]);
    const [kycFiles, setKycFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [upgrading, setUpgrading] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [newDoc, setNewDoc] = useState({
        type: 'Aadhaar',
        number: '',
        file: null
    });

    useEffect(() => {
        getProfile();
    }, []);

    useEffect(() => {
        if (profile && (profile.role === 'owner' || profile.role === 'broker')) {
            getKycDocs();
        }
    }, [profile]);

    const getProfile = async () => {
        try {
            setLoading(true);
            const { data: { user } } = await supabase.auth.getUser();

            if (!user) throw new Error('No user');

            let { data, error, status } = await supabase
                .from('users')
                .select(`*`)
                .eq('id', user.id)
                .single();

            if (error && status !== 406) {
                throw error;
            }

            if (data) {
                setProfile(data);
            }
        } catch (error) {
            console.log('Error loading user data!', error.message);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const getKycDocs = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            const { data, error } = await supabase
                .from('kyc_documents')
                .select('*')
                .eq('user_id', user.id);

            if (error) throw error;
            setKycDocs(data || []);
        } catch (error) {
            console.error('Error fetching KYC docs:', error);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setNewDoc({ ...newDoc, file: e.target.files[0] });
        }
    };

    const [showUpgradeForm, setShowUpgradeForm] = useState(false);
    const [targetRole, setTargetRole] = useState('owner'); // 'owner' | 'broker'

    const handleRoleUpgrade = async (e) => {
        e.preventDefault();
        if (!newDoc.file || !newDoc.number) {
            alert('Please provide document number and file');
            return;
        }

        try {
            setUploading(true);
            const { data: { user } } = await supabase.auth.getUser();

            // 1. Upload File
            const fileExt = newDoc.file.name.split('.').pop();
            const fileName = `${user.id}/${Date.now()}.${fileExt}`;
            const { error: uploadError } = await supabase.storage
                .from('kyc-documents')
                .upload(fileName, newDoc.file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('kyc-documents')
                .getPublicUrl(fileName);

            // 2. Insert KYC Record
            const { error: dbError } = await supabase
                .from('kyc_documents')
                .insert([{
                    user_id: user.id,
                    document_type: newDoc.type,
                    document_number: newDoc.number,
                    document_url: publicUrl,
                    verification_status: 'pending'
                }]);

            if (dbError) throw dbError;

            // 3. Update User Role
            const { error: roleError } = await supabase
                .from('users')
                .update({ role: targetRole })
                .eq('id', user.id);

            if (roleError) throw roleError;

            // 4. Refresh
            await getProfile();
            setShowUpgradeForm(false);
            setNewDoc({ type: 'Aadhaar', number: '', file: null });
            alert(`Success! You are now a ${targetRole}.`);

        } catch (error) {
            console.error('Upgrade failed:', error.message);
            alert('Upgrade failed: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const handleUploadKyc = async (e) => {
        e.preventDefault();
        if (!newDoc.file || !newDoc.number) {
            alert('Please provide document number and file');
            return;
        }

        try {
            setUploading(true);
            const { data: { user } } = await supabase.auth.getUser();

            // 1. Upload file
            const fileExt = newDoc.file.name.split('.').pop();
            const fileName = `${user.id}/${Date.now()}.${fileExt}`;
            const { error: uploadError } = await supabase.storage
                .from('kyc-documents')
                .upload(fileName, newDoc.file);

            if (uploadError) throw uploadError;

            // 2. Get Public URL (or just store path if bucket is private, but let's assume public access for now or signed url needed.
            // The policy says "Users can view own KYC", so maybe private.
            // Store the path key or info to retrieve it.
            // The schema expects 'document_url'. We can store the full path or public URL.
            // Let's store the full path for now or assume public for simplicity if possible, but standard is path.
            // Actually, for private buckets, we usually store the path.
            // But let's try to get a URL.
            const { data: { publicUrl } } = supabase.storage
                .from('kyc-documents')
                .getPublicUrl(fileName);

            // 3. Insert Record
            const { error: dbError } = await supabase
                .from('kyc_documents')
                .insert([{
                    user_id: user.id,
                    document_type: newDoc.type,
                    document_number: newDoc.number,
                    document_url: publicUrl || fileName,
                    verification_status: 'pending'
                }]);

            if (dbError) throw dbError;

            // 4. Refresh
            await getKycDocs();
            setNewDoc({ type: 'Aadhaar', number: '', file: null });
            alert('Document uploaded successfully!');
        } catch (error) {
            console.error('Upload failed:', error.message);
            alert('Upload failed: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const handleLogoutClick = () => {
        setShowLogoutModal(true);
    };

    const confirmLogout = async () => {
        await supabase.auth.signOut();
        setShowLogoutModal(false);
        navigate('/signin');
    };

    if (loading) {
        return (
            <div className="profile-container">
                <Navbar transparent />
                <div style={{ padding: '2rem', textAlign: 'center' }}>Loading profile...</div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="profile-container">
                <Navbar transparent />
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    No profile found. <a href="/signin">Sign In</a>
                </div>
            </div>
        );
    }

    const { full_name, role, phone_number } = profile;
    const initials = full_name ? full_name.charAt(0).toUpperCase() : '?';

    return (
        <div className="page-wrapper profile-page">
            <SEO
                title="My Profile"
                description="Manage your profile and account settings on The Rent Company."
                path="/profile"
            />
            <Navbar transparent />

            {/* Gradient Header Background */}
            <div className="profile-header-bg">
                <div className="profile-header-content">
                    <div className="profile-avatar-large">
                        {initials}
                    </div>
                    <div className="profile-title-section">
                        <h1>{full_name}</h1>
                        <span className="profile-role-badge">{role}</span>
                    </div>
                </div>
            </div>

            <div className="profile-container">
                <div className="profile-card">

                    <div className="profile-section">
                        <h2>Personal Information</h2>
                        <div className="info-grid">

                            <div className="info-item">
                                <span className="info-label">Phone</span>
                                <span className="info-value">{phone_number || 'Not provided'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Full Name</span>
                                <span className="info-value">{full_name}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Member Since</span>
                                <span className="info-value">
                                    {new Date(profile.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Tenant: Upgrade Option */}
                    {role === 'tenant' && (
                        <div className="profile-section">
                            <h2>List Your Property</h2>
                            <p style={{ color: '#666', marginBottom: '1rem' }}>Want to list your property or become a broker? You need to verify your identity first.</p>

                            {!showUpgradeForm ? (
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <Button onClick={() => { setTargetRole('owner'); setShowUpgradeForm(true); }}>
                                        Become an Owner
                                    </Button>
                                    <Button onClick={() => { setTargetRole('broker'); setShowUpgradeForm(true); }} style={{ background: '#4a5568' }}>
                                        Become a Broker
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={handleRoleUpgrade} className="kyc-upload-form" style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: '8px' }}>
                                    <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                                        Verify to become {targetRole === 'owner' ? 'an Owner' : 'a Broker'}
                                    </h3>
                                    <div style={{ display: 'grid', gap: '1rem' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Document Type</label>
                                            <select
                                                value={newDoc.type}
                                                onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value })}
                                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                                            >
                                                <option value="Aadhaar">Aadhaar Card</option>
                                                <option value="PAN">PAN Card</option>
                                                {targetRole === 'owner' && <option value="Ownership Proof">Ownership Proof</option>}
                                                {targetRole === 'broker' && <option value="Broker License">Broker License</option>}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Document Number</label>
                                            <input
                                                type="text"
                                                value={newDoc.number}
                                                onChange={(e) => setNewDoc({ ...newDoc, number: e.target.value })}
                                                placeholder="Enter ID Number"
                                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Choose File</label>
                                            <input
                                                type="file"
                                                onChange={handleFileChange}
                                                style={{ width: '100%' }}
                                                required
                                            />
                                        </div>
                                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                            <Button type="submit" disabled={uploading}>
                                                {uploading ? 'Verifying...' : 'Submit & Upgrade'}
                                            </Button>
                                            <button
                                                type="button"
                                                className="text-btn"
                                                onClick={() => setShowUpgradeForm(false)}
                                                disabled={uploading}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>
                    )}

                    {/* Owner/Broker: Existing KYC Section */}
                    {(role === 'owner' || role === 'broker') && (
                        <div className="profile-section">
                            <h2>KYC Verification</h2>

                            {kycDocs.length === 0 ? (
                                <form onSubmit={handleUploadKyc} className="kyc-upload-form" style={{ marginBottom: '2rem', background: '#f8fafc', padding: '1.5rem', borderRadius: '8px' }}>
                                    <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>Upload New Document</h3>
                                    <div style={{ display: 'grid', gap: '1rem' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Document Type</label>
                                            <select
                                                value={newDoc.type}
                                                onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value })}
                                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                                            >
                                                <option value="Aadhaar">Aadhaar Card</option>
                                                <option value="PAN">PAN Card</option>
                                                <option value="Passport">Passport</option>
                                                <option value="Driving License">Driving License</option>
                                                {role === 'owner' && <option value="Ownership Proof">Ownership Proof</option>}
                                                {role === 'broker' && <option value="Broker License">Broker License</option>}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Document Number</label>
                                            <input
                                                type="text"
                                                value={newDoc.number}
                                                onChange={(e) => setNewDoc({ ...newDoc, number: e.target.value })}
                                                placeholder="Enter ID Number"
                                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Choose File</label>
                                            <input
                                                type="file"
                                                onChange={handleFileChange}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <Button type="submit" disabled={uploading}>
                                            {uploading ? 'Uploading...' : 'Upload Document'}
                                        </Button>
                                    </div>
                                </form>
                            ) : (
                                <div style={{ marginBottom: '2rem', padding: '1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534' }}>
                                    Your documents have been uploaded and are under review.
                                </div>
                            )}

                            {/* List Existing Docs */}
                            <div className="kyc-list">
                                <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>Uploaded Documents</h3>
                                {kycDocs.length === 0 ? (
                                    <p style={{ color: '#666' }}>No documents uploaded yet.</p>
                                ) : (
                                    <div style={{ display: 'grid', gap: '1rem' }}>
                                        {kycDocs.map(doc => (
                                            <div key={doc.id} style={{ border: '1px solid #eee', padding: '1rem', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ fontWeight: '500', textTransform: 'capitalize' }}>{doc.document_type}</div>
                                                    <div style={{ fontSize: '0.875rem', color: '#666' }}>{doc.document_number}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#999' }}>{new Date(doc.created_at).toLocaleDateString()}</div>
                                                </div>
                                                <div>
                                                    <span style={{
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        background: doc.verification_status === 'verified' ? '#dcfce7' : '#fef9c3',
                                                        color: doc.verification_status === 'verified' ? '#166534' : '#854d0e'
                                                    }}>
                                                        {doc.verification_status}
                                                    </span>
                                                    {/* Link to view if needed */}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Logout Section */}
                <div className="profile-card">
                    <div className="profile-section">
                        <h2>Account Actions</h2>
                        <div className="logout-section">
                            <p>Need to sign out? Click the button below to logout from your account.</p>
                            <Button onClick={handleLogoutClick} className="logout-btn">
                                Logout
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmationModal
                isOpen={showLogoutModal}
                title="Confirm Logout"
                message="Are you sure you want to log out?"
                onConfirm={confirmLogout}
                onCancel={() => setShowLogoutModal(false)}
            />
        </div>
    );
};

export default Profile;
